import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { DetailedChangeset } from 'ember-changeset/types';
import { dropTask } from 'ember-concurrency';
import LocationSettingsCapacityLimitModal from 'garaje/components/location-settings/capacity/limit-modal';
import ProtectCapacityLimitsUpsell from 'garaje/components/protect/capacity-limits-upsell';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import PageSection from 'garaje/components/ui/page/section';
import dateFormat from 'garaje/helpers/date-format';
import type Location from 'garaje/models/location';
import type LocationsCapacity from 'garaje/models/locations-capacity';
import type User from 'garaje/models/user';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { fetchCapacities } from 'garaje/utils/locations-capacity';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import _uniqBy from 'lodash/uniqBy';
import moment from 'moment-timezone';
import { all } from 'rsvp';
import { Promise as EmberPromise } from 'rsvp';

const USER_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST].join(',');

interface LocationSettingsCapacitySignature {
  Args: {
    currentLocation: Location;
    changeset: DetailedChangeset<Location>;
  };
  Element: HTMLDivElement;
}

export default class LocationSettingsCapacity extends Component<LocationSettingsCapacitySignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;

  @tracked exeededCapacities: LocationsCapacity[] = [];
  @tracked showUpsell = false;

  resolveCapacityChange: (changed?: boolean) => void = () => {};
  limitChangeset?: DetailedChangeset<Location>;

  @action
  isDirty(): boolean | undefined {
    return this.limitChangeset?.isDirty;
  }

  @action
  rollback(): DetailedChangeset<Location> | undefined {
    return this.limitChangeset?.rollback();
  }

  @action
  registerChangeset(changeset: DetailedChangeset<Location>): void {
    this.limitChangeset = changeset;
  }

  @action
  async save(changeset: DetailedChangeset<Location>): Promise<void> {
    const { flashMessages } = this;
    try {
      await changeset.save();
      flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      throw e;
    }
  }

  @action
  async searchUsers(term: string): Promise<User[]> {
    const { currentLocation } = this.args;
    const userRoles = await this.store.query('user-role', {
      filter: {
        name: term,
        location: currentLocation.id,
        roles: USER_ROLES,
      },
    });
    const users = await all(userRoles.map((role) => role.user));
    return _uniqBy(users, 'id');
  }

  @action
  async validateCapacityChange(capacityLimit: number): Promise<boolean> {
    const { store } = this;
    const { currentLocation } = this.args;
    const today = moment().startOf('day').toDate();
    const future = moment(today).add(1, 'month').toDate();
    const capacities = await fetchCapacities(store, currentLocation, today, future);
    const exeededCapacities = capacities.filter(({ capacityUsed }) => capacityUsed > capacityLimit);
    if (exeededCapacities.length === 0) {
      return true;
    }
    return await this.confirmCapacityModalTask.perform(exeededCapacities);
  }

  confirmCapacityModalTask = dropTask(async (exeededCapacities: LocationsCapacity[] = []): Promise<boolean> => {
    this.exeededCapacities = exeededCapacities;
    return await new EmberPromise((resolve) => {
      this.resolveCapacityChange = (value) => resolve(value);
    });
  });

  @action
  confirmCapacityChange(): void {
    this.resolveCapacityChange(true);
  }

  @action
  cancelCapacityChange(): void {
    this.resolveCapacityChange(false);
  }

  <template>
    <PageSection data-test-location-capacity-settings-panel>
      <h3>Capacity</h3>

      <div class={{if this.showUpsell "opacity-50"}}>
        <LocationSettingsCapacityLimitModal
          data-test-capacity-limit-panel
          @disabled={{this.showUpsell}}
          @onSubmit={{this.save}}
          @onConfirmCapacity={{this.validateCapacityChange}}
          @onSearchUsers={{this.searchUsers}}
          @location={{@currentLocation}}
          @registerChangeset={{this.registerChangeset}}
          @changeset={{@changeset}}
        />
      </div>

      {{#if this.showUpsell}}
        <ProtectCapacityLimitsUpsell />
      {{/if}}

      {{#if this.confirmCapacityModalTask.isRunning}}
        <ConfirmationModal
          @title="Location at capacity"
          @ctaText="Continue"
          @onClose={{this.cancelCapacityChange}}
          @handleTaskAction={{this.confirmCapacityChange}}
        >
          <p class="mt-4">
            Changing capacity will cause the following dates to exceed capacity:
          </p>
          <ul>
            {{#each this.exeededCapacities as |capacity|}}
              <li>{{dateFormat capacity.capacityDate "EEE, MMMM d, yyyy"}}</li>
            {{/each}}
          </ul>
          <p>
            Are you sure you’d like to continue?
          </p>
        </ConfirmationModal>
      {{/if}}
    </PageSection>
  </template>
}
